import { MutationObserverOptions, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed } from 'vue';

import { SPLIT_NAMES } from '@/constants';
import { useUserStore } from '@/piniaStores/user/user.store';
import { PaymentOnSellThroughEnrollment } from '@/services/api/models/paymentOnSellThrough';
import {
  acceptPaymentOnSellThroughTerms,
  acknowledgePaymentOnSellThroughEnrollment,
  getPaymentOnSellThroughEnrollment,
  paymentOnSellThroughOptIn,
  rejectPaymentOnSellThroughEnrollment,
  removePaymentOnSellThroughCompletionMessage,
} from '@/services/api/paymentOnSellThrough';

export const paymentOnSellThroughEnrollmentQueryKeys = {
  enrollmentState: () => ['paymentOnSellThroughEnrollment'],
};

export function usePaymentOnSellThroughEnrollmentQuery() {
  const { getIsSplitEnabled } = useUserStore();
  return useQuery({
    queryKey: paymentOnSellThroughEnrollmentQueryKeys.enrollmentState(),
    queryFn: () => getPaymentOnSellThroughEnrollment(),
    enabled: computed(() => getIsSplitEnabled(SPLIT_NAMES.PAYMENT_ON_SELL_THROUGH_ENROLLMENT)),
  });
}

export function useInvalidatePaymentOnSellThroughEnrollmentQuery() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: paymentOnSellThroughEnrollmentQueryKeys.enrollmentState() });
}

export function useAcknowledgePostEnrollmentMutation() {
  const invalidateQueries = useInvalidatePaymentOnSellThroughEnrollmentQuery();
  return useMutation({
    mutationFn: () => acknowledgePaymentOnSellThroughEnrollment(),
    onSettled: () => invalidateQueries(),
  });
}

export function useRejectPostEnrollmentMutation() {
  const invalidateQueries = useInvalidatePaymentOnSellThroughEnrollmentQuery();
  return useMutation({
    mutationFn: ({ text }: { text: string }) => rejectPaymentOnSellThroughEnrollment({ text }),
    onSettled: () => invalidateQueries(),
  });
}

export function useAcceptPostTermsMutation() {
  const invalidateQueries = useInvalidatePaymentOnSellThroughEnrollmentQuery();
  return useMutation({
    mutationFn: () => acceptPaymentOnSellThroughTerms(),
    onSettled: () => invalidateQueries(),
  });
}

export function useRemovePostEnrollmentCompletMutation() {
  return useMutation({
    mutationFn: () => removePaymentOnSellThroughCompletionMessage(),
  });
}

export function usePaymentOnSellThroughOptInMutation(
  options?: MutationObserverOptions<PaymentOnSellThroughEnrollment>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => paymentOnSellThroughOptIn(),
    ...options,
    onSuccess: (data, variables, ctx) => {
      options?.onSuccess?.(data, variables, ctx);

      queryClient.invalidateQueries({ queryKey: paymentOnSellThroughEnrollmentQueryKeys.enrollmentState() });
    },
  });
}
